/**
 * API 客戶端
 * 統一管理對 towconnect-api 的認證狀態
 * Services 無需關心登入邏輯
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import type { ApiResponse } from '@/types'

/**
 * API 業務錯誤
 * 當後端返回 HTTP 200 但 statusCode 不是 "200"/"201" 時拋出
 */
export class ApiBusinessError extends Error {
  code: string
  statusDesc: string

  constructor(code: string, statusDesc: string) {
    super(statusDesc)
    this.name = 'ApiBusinessError'
    this.code = code
    this.statusDesc = statusDesc
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'

// ========== Token Storage Keys ==========
const TOKEN_KEYS = {
  accessToken: 'towconnect-access-token',
  refreshToken: 'towconnect-refresh-token',
  accessTokenExpires: 'towconnect-access-token-expires',
  refreshTokenExpires: 'towconnect-refresh-token-expires',
  lineIdToken: 'towconnect-liff-id-token',
  liffUser: 'towconnect-liff-user',
} as const

// ========== Types ==========
interface LineLoginResponse {
  bound: boolean
  accessToken: string
  refreshToken: string
  accessTokenExpiresIn: number
  refreshTokenExpiresIn: number
  driverId: string | null
  driverName: string | null
  companyCode: string | null
  companyName: string | null
}

interface RefreshTokenResponse {
  accessToken: string
  refreshToken: string
  accessTokenExpiresIn: number
  refreshTokenExpiresIn: number
}

export interface LineLoginResult {
  success: boolean
  message: string
  driverId?: string
  driverName?: string
  companyCode?: string
  companyName?: string
  accessToken?: string
  refreshToken?: string
  accessTokenExpiresIn?: number
  refreshTokenExpiresIn?: number
}

class ApiClient {
  private client: AxiosInstance
  private isRefreshing = false
  private refreshPromise: Promise<string | null> | null = null

  // 認證相關端點，這些端點不需要自動 login/refresh 處理
  private static AUTH_ENDPOINTS = ['/line/auth/login', '/line/auth/refresh']

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupRequestInterceptor()
    this.setupResponseInterceptor()
  }

  // ========== Interceptors Setup ==========

  private setupRequestInterceptor(): void {
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // 跳過認證相關端點
        if (this.isAuthEndpoint(config.url)) {
          return config
        }

        // 確保有系統 Token
        let accessToken = this.getValidAccessToken()

        if (!accessToken) {
          // 沒有系統 Token，嘗試用 LINE ID Token 登入
          accessToken = await this.ensureAuthenticated()
        }

        if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`
        }

        // 同時也傳送 X-Line-User-Id（為了向後相容）
        const lineUserId = this.getLineUserIdFromStorage()
        if (lineUserId) {
          config.headers['X-Line-User-Id'] = lineUserId
        }

        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  private setupResponseInterceptor(): void {
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response
      },
      async (error) => {
        const originalRequest = error.config

        // 401 未授權 - 嘗試 refresh token
        if (error.response?.status === 401 && !this.isAuthEndpoint(originalRequest?.url)) {
          // 避免無限重試
          if (originalRequest._retry) {
            this.redirectToLogin()
            return Promise.reject(error)
          }

          originalRequest._retry = true

          const newToken = await this.tryRefreshToken()
          if (newToken) {
            // 重試原始請求
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`
            return this.client.request(originalRequest)
          }

          // Refresh 失敗，重定向到首頁
          this.redirectToLogin()
          return Promise.reject(error)
        }

        // 403 禁止訪問 — 後端不會告訴前端是「沒認證」還是「已認證但未綁定」，
        // 而 localStorage 裡的 token 就算 exp 未到也可能已經無效
        // （簽章對不上：例如 bundle 曾指到別的環境、後端換過 JWT_SECRET；或被列入黑名單）。
        // 所以不再相信 client 端的過期時間，改用 LIFF ID Token 重打一次 /api/line/auth/login，
        // 拿後端的回答當唯一依據：
        //   - 已綁定 → 換到新 token，重送原請求一次
        //   - 未綁定 → 已拿到 LINE_USER token，導綁定頁
        //   - 登入本身失敗（ID Token 過期、伺服器錯誤）→ 清 token 回首頁重跑 LIFF 登入
        // 2026-09-14 事故：對外 LIFF 存著內部 api 簽的 token，舊邏輯信 exp 直接導綁定頁，
        // 綁定 API 同樣 403 → 無限打轉。
        if (error.response?.status === 403 && !this.isAuthEndpoint(originalRequest?.url)) {
          if (originalRequest._retry403) {
            // 已用新 token 重送過仍 403：是真正的權限拒絕，不再重試也不再導頁，交給呼叫端
            console.error('[ApiClient] 重新登入後仍 403，放棄:', error.response.data)
            return Promise.reject(error)
          }
          originalRequest._retry403 = true

          const lineIdToken = localStorage.getItem(TOKEN_KEYS.lineIdToken)
          if (!lineIdToken) {
            console.warn('[ApiClient] 403 且無 LINE ID Token，回首頁重新登入')
            this.redirectToLogin()
            return Promise.reject(error)
          }

          console.warn('[ApiClient] 403，改用 LINE ID Token 重新登入以確認狀態')
          const login = await this.lineLogin(lineIdToken)

          if (login.success && login.accessToken) {
            originalRequest.headers['Authorization'] = `Bearer ${login.accessToken}`
            return this.client.request(originalRequest)
          }

          if (login.accessToken) {
            // 後端確認未綁定（token 已存，綁定頁會用）；已在綁定頁就不重載，避免自己導自己
            console.warn('[ApiClient] 後端確認尚未綁定司機，導向綁定頁')
            if (window.location.pathname !== '/binding') {
              window.location.href = '/binding'
            }
            return Promise.reject(error)
          }

          console.warn('[ApiClient] 重新登入失敗，回首頁重跑 LIFF 登入:', login.message)
          this.redirectToLogin()
          return Promise.reject(error)
        }

        // 500 伺服器錯誤
        if (error.response?.status >= 500) {
          console.error('Server error:', error.response.data)
        }

        return Promise.reject(error)
      }
    )
  }

  // ========== Authentication Helpers ==========

  /**
   * 檢查是否為認證相關端點
   */
  private isAuthEndpoint(url: string | undefined): boolean {
    if (!url) return false
    return ApiClient.AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint))
  }

  /**
   * 取得有效的 Access Token（未過期）
   */
  private getValidAccessToken(): string | null {
    const token = localStorage.getItem(TOKEN_KEYS.accessToken)
    const expires = localStorage.getItem(TOKEN_KEYS.accessTokenExpires)

    if (token && expires) {
      const expiresAt = parseInt(expires, 10)
      // 提前 30 秒視為過期，避免邊界情況
      if (Date.now() < expiresAt - 30000) {
        return token
      }
    }

    return null
  }

  /**
   * 取得 Refresh Token
   */
  private getValidRefreshToken(): string | null {
    const token = localStorage.getItem(TOKEN_KEYS.refreshToken)
    const expires = localStorage.getItem(TOKEN_KEYS.refreshTokenExpires)

    if (token && expires) {
      const expiresAt = parseInt(expires, 10)
      if (Date.now() < expiresAt) {
        return token
      }
    }

    return null
  }

  /**
   * 從 localStorage 取得 LINE User ID
   */
  private getLineUserIdFromStorage(): string | null {
    const userJson = localStorage.getItem(TOKEN_KEYS.liffUser)
    if (userJson) {
      try {
        const user = JSON.parse(userJson)
        return user.userId || null
      } catch {
        return null
      }
    }
    return null
  }

  /**
   * 儲存 tokens 到 localStorage
   */
  private saveTokens(
    accessToken: string,
    refreshToken: string,
    accessTokenExpiresIn: number,
    refreshTokenExpiresIn: number
  ): void {
    localStorage.setItem(TOKEN_KEYS.accessToken, accessToken)
    localStorage.setItem(TOKEN_KEYS.refreshToken, refreshToken)
    localStorage.setItem(TOKEN_KEYS.accessTokenExpires, String(Date.now() + accessTokenExpiresIn * 1000))
    localStorage.setItem(TOKEN_KEYS.refreshTokenExpires, String(Date.now() + refreshTokenExpiresIn * 1000))
  }

  /**
   * 清除所有認證資料
   */
  private clearAuthData(): void {
    localStorage.removeItem(TOKEN_KEYS.accessToken)
    localStorage.removeItem(TOKEN_KEYS.refreshToken)
    localStorage.removeItem(TOKEN_KEYS.accessTokenExpires)
    localStorage.removeItem(TOKEN_KEYS.refreshTokenExpires)
    localStorage.removeItem('driver-binding')
  }

  /**
   * 重定向到首頁讓用戶重新 LIFF 登入
   */
  private redirectToLogin(): void {
    this.clearAuthData()
    window.location.href = '/'
  }

  /**
   * 確保已認證（有 LINE ID Token 則自動 login）
   */
  private async ensureAuthenticated(): Promise<string | null> {
    const lineIdToken = localStorage.getItem(TOKEN_KEYS.lineIdToken)
    if (!lineIdToken) {
      console.warn('[ApiClient] 缺少 LINE ID Token，請先完成 LIFF 登入')
      this.redirectToLogin()
      return null
    }

    // 調用 /api/line/auth/login
    const result = await this.performLineLogin(lineIdToken)
    return result
  }

  /**
   * 執行 LINE 登入
   */
  private async performLineLogin(lineIdToken: string): Promise<string | null> {
    const lineUserId = this.getLineUserIdFromStorage()
    console.log('[ApiClient] performLineLogin - X-Line-User-Id:', lineUserId)

    try {
      const response = await axios.post<{ statusCode: string; statusDesc: string; data: LineLoginResponse }>(
        `${API_BASE_URL}/line/auth/login`,
        {},
        {
          headers: {
            Authorization: `Bearer ${lineIdToken}`,
            ...(lineUserId && { 'X-Line-User-Id': lineUserId }),
          },
        }
      )

      const apiResponse = response.data

      if (apiResponse.statusCode !== '200') {
        console.error('[ApiClient] LINE 登入失敗:', apiResponse.statusDesc)
        return null
      }

      const data = apiResponse.data

      // 儲存 tokens
      this.saveTokens(data.accessToken, data.refreshToken, data.accessTokenExpiresIn, data.refreshTokenExpiresIn)

      console.log('[ApiClient] LINE 登入成功, bound:', data.bound)
      return data.accessToken
    } catch (error) {
      console.error('[ApiClient] LINE 登入錯誤:', error)
      return null
    }
  }

  /**
   * 嘗試 refresh token（防止並發 refresh）
   */
  private async tryRefreshToken(): Promise<string | null> {
    // 如果已經在 refresh，等待結果
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise
    }

    const refreshToken = this.getValidRefreshToken()
    if (!refreshToken) {
      console.warn('[ApiClient] Refresh token 不存在或已過期')
      return null
    }

    this.isRefreshing = true
    this.refreshPromise = this.performRefresh(refreshToken)

    try {
      return await this.refreshPromise
    } finally {
      this.isRefreshing = false
      this.refreshPromise = null
    }
  }

  /**
   * 執行 token refresh
   */
  private async performRefresh(refreshToken: string): Promise<string | null> {
    console.log('[ApiClient] 執行 token refresh...')

    try {
      const response = await axios.post<{ statusCode: string; statusDesc: string; data: RefreshTokenResponse }>(
        `${API_BASE_URL}/line/auth/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        }
      )

      const apiResponse = response.data

      if (apiResponse.statusCode !== '200') {
        console.error('[ApiClient] Token refresh 失敗:', apiResponse.statusDesc)
        return null
      }

      const data = apiResponse.data

      // 儲存新 tokens
      this.saveTokens(data.accessToken, data.refreshToken, data.accessTokenExpiresIn, data.refreshTokenExpiresIn)

      console.log('[ApiClient] Token refresh 成功')
      return data.accessToken
    } catch (error) {
      console.error('[ApiClient] Token refresh 錯誤:', error)
      return null
    }
  }

  // ========== 檢查業務邏輯錯誤 ==========
  private checkBusinessError<T>(response: AxiosResponse<ApiResponse<T>>): T {
    const { statusCode, statusDesc, data } = response.data
    // 成功的 statusCode 為 "200" 或 "201"
    if (statusCode !== '200' && statusCode !== '201') {
      throw new ApiBusinessError(statusCode, statusDesc)
    }
    return data as T
  }

  // ========== GET ==========
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<ApiResponse<T>>(url, config)
    return this.checkBusinessError(response)
  }

  // ========== POST ==========
  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<ApiResponse<T>>(url, data, config)
    return this.checkBusinessError(response)
  }

  // ========== PUT ==========
  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config)
    return this.checkBusinessError(response)
  }

  // ========== PATCH ==========
  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, config)
    return this.checkBusinessError(response)
  }

  // ========== DELETE ==========
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<ApiResponse<T>>(url, config)
    return this.checkBusinessError(response)
  }

  // ========== 原始 Axios 實例（進階使用） ==========
  getAxiosInstance(): AxiosInstance {
    return this.client
  }

  // ========== Public Token Management ==========

  /**
   * 取得儲存的 Access Token（供外部使用）
   */
  getStoredAccessToken(): string | null {
    return this.getValidAccessToken()
  }

  /**
   * 取得儲存的 Refresh Token（供外部使用）
   */
  getStoredRefreshToken(): string | null {
    return this.getValidRefreshToken()
  }

  /**
   * 儲存 tokens（供 bind 成功後使用）
   */
  saveAuthTokens(
    accessToken: string,
    refreshToken: string,
    accessTokenExpiresIn: number,
    refreshTokenExpiresIn: number
  ): void {
    this.saveTokens(accessToken, refreshToken, accessTokenExpiresIn, refreshTokenExpiresIn)
  }

  /**
   * 清除認證資料（供登出使用）
   */
  clearAuth(): void {
    this.clearAuthData()
  }

  // ========== Public LINE Login ==========

  /**
   * LINE 登入（供初始登入流程使用）
   *
   * <p>使用 LINE ID Token 登入，取得 accessToken 和 refreshToken。
   * 回傳完整的登入結果供呼叫端判斷是否已綁定。
   *
   * @param lineIdToken LINE ID Token (從 LIFF 取得)
   * @returns 登入結果（包含 token 和司機資訊）
   */
  async lineLogin(lineIdToken: string): Promise<LineLoginResult> {
    const lineUserId = this.getLineUserIdFromStorage()
    console.log('[ApiClient] lineLogin - X-Line-User-Id:', lineUserId)

    try {
      const response = await axios.post<{ statusCode: string; statusDesc: string; data: LineLoginResponse }>(
        `${API_BASE_URL}/line/auth/login`,
        {},
        {
          headers: {
            Authorization: `Bearer ${lineIdToken}`,
            ...(lineUserId && { 'X-Line-User-Id': lineUserId }),
          },
        }
      )

      const apiResponse = response.data

      // 檢查 statusCode 是否為成功
      if (apiResponse.statusCode !== '200') {
        return {
          success: false,
          message: apiResponse.statusDesc || '登入失敗',
        }
      }

      const data = apiResponse.data

      // 無論是否已綁定，都儲存 token（未綁定時會取得 LINE_USER JWT 用於綁定）
      this.saveTokens(data.accessToken, data.refreshToken, data.accessTokenExpiresIn, data.refreshTokenExpiresIn)

      // 檢查是否已綁定
      if (!data.bound) {
        // 未綁定：回傳 success=false，但 token 已儲存供綁定使用
        return {
          success: false,
          message: 'LINE 帳號尚未綁定司機',
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          accessTokenExpiresIn: data.accessTokenExpiresIn,
          refreshTokenExpiresIn: data.refreshTokenExpiresIn,
        }
      }

      // 已綁定：回傳完整司機資訊
      return {
        success: true,
        message: '登入成功',
        driverId: data.driverId ?? undefined,
        driverName: data.driverName ?? undefined,
        companyCode: data.companyCode ?? undefined,
        companyName: data.companyName ?? undefined,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        accessTokenExpiresIn: data.accessTokenExpiresIn,
        refreshTokenExpiresIn: data.refreshTokenExpiresIn,
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const apiMessage = error.response?.data?.statusDesc || error.response?.data?.message || error.message

        return {
          success: false,
          message: apiMessage || '登入失敗，請稍後再試',
        }
      }

      return {
        success: false,
        message: '網路錯誤，請檢查連線',
      }
    }
  }
}

// Singleton 模式
const apiClient = new ApiClient()
export default apiClient

// 匯出 token 管理函數（向後相容）
export const getStoredAccessToken = () => apiClient.getStoredAccessToken()
export const getStoredRefreshToken = () => apiClient.getStoredRefreshToken()
export const clearAuthData = () => apiClient.clearAuth()

// 匯出 LINE 登入函數（供初始登入流程使用）
export const lineLogin = (lineIdToken: string) => apiClient.lineLogin(lineIdToken)
